import Footer from 'app/components/FooterComponent';
import Header from 'app/components/HeaderComponent';
import HeaderImage from 'home/components/HeaderImage';
import React from 'react';
import { Alert, Box, Button, Divider, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link } from 'react-router-dom';
import { REGISTRATION_REQUEST, USER_CLEAR_REGISTRATION_ERROR } from 'actions';
import config from 'config';
import AuthLayout from '_containers/AuthLayout';

type State = {
  email: string,
  password: string,
  repeatedPassword: string,
  errors: { [fieldName: string]: string[] },
  registered: boolean,
  touched: boolean,
  showPassword: boolean,
  showRepeatedPassword: boolean,
};

type Props = {
  dispatch: Function,
  user: any,
};  

export default class Registration extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    if (config.domainName !== 'localhost') {
      window.location = config.ssoRegistrationUrl;
    }
  }

  state = { email: '', password: '', repeatedPassword: '', errors: {}, registered: false, touched: false, showPassword: false, showRepeatedPassword: false };

  checkAndClearErrors() {
    const { errors } = this.state;
    const { dispatch } = this.props;
    if (errors && Object.keys(errors).length > 0) {
      dispatch(USER_CLEAR_REGISTRATION_ERROR());
      this.setState({ errors: {} });
    }
  }

  setEmail = (email) => {
    this.setState(() => ({ email }));
    this.checkAndClearErrors();
  }

  setPassword = (password) => {
    this.setState(() => ({ password }));
    this.checkAndClearErrors();
  }

  setRepeatedPassword = (repeatedPassword) => {
    this.setState(() => ({ repeatedPassword }));
    this.checkAndClearErrors();
  }

  handleKeyPressOnFormInput = (e) => {
    if (e.key === 'Enter') {
      this.submitRegistrationFrom();
    }
  }

  isEmailValid = () => /^\S+@\S+\.\S+$/.test(this.state.email.trim())

  isFormValidate = () => {
    const { email, password, repeatedPassword } = this.state;

    if (!this.isEmailValid()) {
      this.setState(() => ({ errors: { email: ['Enter a valid email address.'] } }));
      return false;
    }

    if (password.length < 8) {
      this.setState(() => ({ errors: { password: ['Password must be at least 8 characters.'] } }));
      return false;
    }

    if (password !== repeatedPassword) {
      this.setState(() => ({
        errors: {
          password: ['Passwords do not match.'],
          repeatedPassword: ['Passwords do not match.'],
        },
      }));

      return false;
    }

    return email.length > 0 && password.length > 0 && repeatedPassword.length > 0;
  }

  submitRegistrationFrom = () => {
    const { email, password, repeatedPassword } = this.state;
    const { dispatch } = this.props;
    this.setState({ touched: true });

    if (!this.isFormValidate()) {
      return;
    }

    dispatch(REGISTRATION_REQUEST(
      email.trim(),
      password,
      repeatedPassword,
      this.postRegistrationAction,
      this.onErrorAction,
    ));
  }

  clearRegistrationFrom = () => {
    this.setState(() => ({
      email: '',
      password: '',
      repeatedPassword: '',
      errors: {},
      touched: false,
    }));
  }

  postRegistrationAction = () => {
    this.setState(() => ({
      email: '',
      password: '',
      repeatedPassword: '',
      errors: {},
      registered: true,
      touched: false,
    }));
  }

  onErrorAction = (response: { [fieldName: string]: string[] }) => {
    this.setState(() => ({ errors: response }));
  }

  getFieldError = (fieldName: string) => {
    const { errors } = this.state;

    if (!errors || Object.keys(errors).length === 0 || !errors[fieldName]) {
      return '';
    }

    return errors[fieldName].join(' ');
  }

  printNonFieldsError = () => {
    const error = this.getFieldError('non_field_errors');
    return error ? <Alert severity="error" className="auth-alert">{error}</Alert> : null;
  }

  registrationForm = () => {
    const { email, password, repeatedPassword, touched, showPassword, showRepeatedPassword } = this.state;
    const { user } = this.props;
    const emailError = this.getFieldError('email') || (touched && !this.isEmailValid() ? 'Enter a valid email address.' : '');
    const passwordError = this.getFieldError('password') || (touched && password.length < 8 ? 'Password must be at least 8 characters.' : '');
    const repeatedPasswordError = this.getFieldError('repeatedPassword') || (touched && password !== repeatedPassword ? 'Passwords do not match.' : '');

    return (
      <Box component="form" id="registration-form" className="auth-form" noValidate>
        <Box className="auth-field">
          <label htmlFor="email">Email address</label>
          <TextField
            id="email"
            type="email"
            placeholder="your.email@example.com"
            value={email}
            fullWidth
            size="small"
            variant="outlined"
            className="auth-input"
            error={Boolean(emailError)}
            helperText={emailError || undefined}
            onKeyDown={this.handleKeyPressOnFormInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setEmail(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon className="auth-input-icon" /></InputAdornment> }}
          />
        </Box>
        <Box className="auth-field">
          <label htmlFor="password">Password</label>
          <TextField
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a password"
            value={password}
            fullWidth
            size="small"
            variant="outlined"
            className="auth-input"
            error={Boolean(passwordError)}
            helperText={passwordError || 'Use at least 8 characters.'}
            onKeyDown={this.handleKeyPressOnFormInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setPassword(event.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon className="auth-input-icon" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton className="auth-visibility-toggle" onClick={() => this.setState({ showPassword: !showPassword })} edge="end" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="auth-field">
          <label htmlFor="repeatedPassword">Confirm password</label>
          <TextField
            id="repeatedPassword"
            type={showRepeatedPassword ? 'text' : 'password'}
            placeholder="Repeat your password"
            value={repeatedPassword}
            fullWidth
            size="small"
            variant="outlined"
            className="auth-input"
            error={Boolean(repeatedPasswordError)}
            helperText={repeatedPasswordError || undefined}
            onKeyDown={this.handleKeyPressOnFormInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setRepeatedPassword(event.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><LockIcon className="auth-input-icon" /></InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton className="auth-visibility-toggle" onClick={() => this.setState({ showRepeatedPassword: !showRepeatedPassword })} edge="end" aria-label={showRepeatedPassword ? 'Hide repeated password' : 'Show repeated password'}>
                    {showRepeatedPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Button variant="contained" className="auth-primary-button" type="button" id="registration_button" fullWidth startIcon={<PersonAddIcon />} disabled={user.isRunning} onClick={this.submitRegistrationFrom}>
          {user.isRunning ? 'Creating account...' : 'Create account'}
        </Button>
        <Button variant="text" className="auth-clear-button" type="button" id="clear_registration_button" fullWidth onClick={this.clearRegistrationFrom}>
          Clear form
        </Button>
      </Box>
    );
  }

  registeredSuccessfuly = () => {
    return (
      <Alert severity="success" className="auth-alert">
        Account created successfully. You can log in <Link to="/login">here</Link>.
      </Alert>
    );
  }

  content = () => {
    const { registered } = this.state;
    const form = registered ? this.registeredSuccessfuly() : this.registrationForm();
    return (
      <AuthLayout
        title="Create your account"
        subtitle="Set up your account to start designing"
        brandDescription="Create your account to start a project and keep your BOM in sync."
        avatarIcon={<PersonAddIcon />}
        cardClassName="auth-layout--registration"
      >
        {this.printNonFieldsError()}
        {form}
        <Box className="auth-links auth-links--registration">
          <Divider className="auth-divider" />
          <Box className="auth-account-prompt">
            <Typography variant="body2" component="span" className="auth-prompt-text">Already have an account? </Typography>
            <Link to="/login" className="auth-link">Sign in</Link>
          </Box>
        </Box>
      </AuthLayout>
    );
  }

  render() {
    return (
      <div className="app-content registration-page auth-page">
        <Header />
        <HeaderImage content={this.content()} />
        <Footer />
      </div>
    );
  }
}
